import type { AppLocale } from "@/i18n/constants";

type Copy = { es: string; en: string };

export type AdvisoryChoice = {
  id: string;
  /** Exact option text stored by the original Google Form. */
  value: string;
  label: Copy;
};

export type AdvisoryField = {
  id: string;
  /** Google Form `entry` id. */
  entry: number;
  kind: "short" | "long" | "single";
  required: boolean;
  label: Copy;
  options?: readonly AdvisoryChoice[];
  autoComplete?: string;
  inputType?: "text" | "email";
};

export type AdvisorySection = {
  id: string;
  title: Copy;
  description?: Copy;
  fields: readonly AdvisoryField[];
};

export function formText(copy: Copy, locale: AppLocale): string {
  return copy[locale];
}

export const ADVISORY_FORM_INTRO: Copy = {
  es: "Qué alegría que empecemos a trabajar juntas en potenciar tu marca. Este formulario es el primer paso de tu asesoría estratégica: con tus respuestas analizo tu cuenta antes de la llamada. Respondé con el mayor detalle posible. No te dejes nada afuera.",
  en: "I’m glad we’re starting this work on your brand together. This form is the first step of your strategic advisory: your answers let me study your account before the call. Please answer in as much detail as you can, and don’t leave anything out.",
};

export const ADVISORY_FORM_UI = {
  title: { es: "Reservar asesoría estratégica", en: "Book strategic advisory" },
  crumb: { es: "Asesoría estratégica", en: "Strategic advisory" },
  step: { es: "Paso {current} de {total}", en: "Step {current} of {total}" },
  requiredMark: { es: "Obligatorio", en: "Required" },
  back: { es: "Atrás", en: "Back" },
  next: { es: "Siguiente", en: "Next" },
  submit: { es: "Enviar y elegir horario", en: "Send and choose a time" },
  nameError: { es: "Escribí tu nombre.", en: "Enter your name." },
  emailError: { es: "Escribí un correo válido.", en: "Enter a valid email." },
  requiredError: { es: "Esta respuesta es obligatoria.", en: "This answer is required." },
  privacyError: {
    es: "Aceptá la política de privacidad para continuar.",
    en: "Accept the privacy policy to continue.",
  },
  submitError: {
    es: "No pudimos enviar el formulario. Intentá de nuevo.",
    en: "We couldn’t send the form. Please try again.",
  },
  successTitle: { es: "Listo. Ahora elegí el horario.", en: "Done. Now choose a time." },
  successBody: {
    es: "Recibimos tus respuestas. Se abre el calendario para reservar la asesoría estratégica.",
    en: "We’ve received your answers. The calendar opens so you can book the strategic advisory.",
  },
  successAction: { es: "Elegir horario", en: "Choose a time" },
  progressLabel: { es: "Progreso del formulario", en: "Form progress" },
} as const;

const choice = (id: string, value: string, en: string, es = value): AdvisoryChoice => ({
  id,
  value,
  label: { es, en },
});

export const ADVISORY_SECTIONS: readonly AdvisorySection[] = [
  {
    id: "contact",
    title: { es: "Tus datos", en: "Your details" },
    fields: [
      {
        id: "name",
        entry: 1591633300,
        kind: "short",
        required: true,
        autoComplete: "name",
        label: { es: "Nombre", en: "Name" },
      },
      {
        id: "email",
        entry: 699322196,
        kind: "short",
        required: true,
        inputType: "email",
        autoComplete: "email",
        label: { es: "Correo electrónico", en: "Email" },
      },
    ],
  },
  {
    id: "business",
    title: { es: "Tu negocio y tus ventas", en: "Your business and your sales" },
    fields: [
      {
        id: "instagram",
        entry: 2130981258,
        kind: "long",
        required: false,
        label: {
          es: "¿Cuál es el nombre de tu cuenta de Instagram (usuario) y a qué se dedica exactamente tu emprendimiento?",
          en: "What is your Instagram account name (username), and what exactly does your business do?",
        },
      },
      {
        id: "offer",
        entry: 193709734,
        kind: "long",
        required: false,
        label: {
          es: "¿Qué producto o servicio específico te interesa potenciar o vender más en este momento a través de tus redes?",
          en: "Which specific product or service do you most want to promote or sell more of on social media right now?",
        },
      },
      {
        id: "customer",
        entry: 829277189,
        kind: "long",
        required: false,
        label: {
          es: "Si pudieras definir a tu cliente ideal (la persona que hoy más te compra o a la que querés llegar), ¿quién es, qué intereses tiene y qué problemas busca resolver con lo que vos ofrecés?",
          en: "If you could describe your ideal client (the person who buys from you most today, or the person you want to reach), who are they, what are they interested in, and which problems do they want to solve with what you offer?",
        },
      },
      {
        id: "objections",
        entry: 785627355,
        kind: "long",
        required: false,
        label: {
          es: "¿Cuáles son las 3 preguntas o dudas que tus clientes siempre te hacen antes de comprarte? (Ej: envíos, precios, cómo elegir el talle, materiales, stock).",
          en: "What are the 3 questions or doubts clients always raise before they buy? (For example: shipping, prices, how to choose a size, materials, stock).",
        },
      },
    ],
  },
  {
    id: "stats",
    title: { es: "Tus estadísticas y tu comunidad", en: "Your stats and your community" },
    fields: [
      {
        id: "numbers",
        entry: 1379677801,
        kind: "long",
        required: false,
        label: {
          es: "Hablemos de números y resultados actuales: aproximadamente, ¿cuántos seguidores tenés? ¿Notás si tus publicaciones tienen guardados o compartidos? ¿Te llega gente seguido al mensaje privado preguntando precios?",
          en: "Let’s talk about your current numbers and results: roughly how many followers do you have? Do you notice saves or shares on your posts? Do people often message you in private to ask about prices?",
        },
      },
      {
        id: "reach",
        entry: 639457493,
        kind: "long",
        required: false,
        label: {
          es: "¿Sentís que tu contenido le llega siempre a la misma gente (amigos o familiares) o que tu cuenta se estancó y no lográs alcance con personas nuevas?",
          en: "Do you feel your content always reaches the same people (friends or family), or that the account has stalled and you can’t reach new people?",
        },
      },
      {
        id: "stats-where",
        entry: 1890872317,
        kind: "single",
        required: true,
        label: {
          es: "¿Sabés exactamente en qué parte de Instagram se miran las estadísticas generales de tu cuenta y las métricas individuales de cada publicación?",
          en: "Do you know exactly where on Instagram to see your account’s overall stats and the metrics of each post?",
        },
        options: [
          choice(
            "stats-yes",
            "Sí, sé perfectamente dónde encontrarlas y cómo mirarlas.",
            "Yes, I know exactly where to find them and how to read them.",
          ),
          choice(
            "stats-some",
            "Sé que existen y las vi alguna vez, pero no suelo revisarlas ni sé bien dónde están todos los botones.",
            "I know they exist and I’ve seen them once, but I don’t usually check them and I’m not sure where all the buttons are.",
          ),
          choice(
            "stats-no",
            "No, no sé dónde se miran las estadísticas/métricas.",
            "No, I don’t know where to look at the stats.",
          ),
        ],
      },
      {
        id: "stats-use",
        entry: 1680481741,
        kind: "single",
        required: false,
        label: {
          es: "Más allá de los «Me Gusta», ¿sabés usar las estadísticas de forma estratégica para tu negocio? (Por ejemplo: entender qué significa que un posteo tenga muchos guardados o compartidos, y cómo usar ese dato para planificar tu contenido o vender más).",
          en: "Beyond likes, do you know how to use stats strategically for your business? (For example: understanding what it means when a post gets lots of saves or shares, and how to use that to plan content or sell more).",
        },
        options: [
          choice(
            "use-yes",
            "Sí, las analizo y sé cómo usarlas a mi favor.",
            "Yes, I analyse them and I know how to use them in my favour.",
          ),
          choice(
            "use-some",
            "Entiendo un poco, pero me cuesta un montón darme cuenta de qué cambios hacer en mi cuenta basándome en esos números.",
            "I understand a little, but I really struggle to see what to change on my account based on those numbers.",
          ),
          choice(
            "use-no",
            "No, miro los números pero no sé cómo usarlos de forma estratégica para vender.",
            "No, I look at the numbers but I don’t know how to use them strategically to sell.",
          ),
        ],
      },
      {
        id: "best-post",
        entry: 1616065336,
        kind: "long",
        required: false,
        label: {
          es: "Pensando en tus últimos posteos, ¿recordás cuál fue la publicación que mejor funcionó (ya sea en Me Gusta, comentarios, guardados o compartidos) y de qué trataba?",
          en: "Thinking about your recent posts, do you remember which one performed best (likes, comments, saves or shares) and what it was about?",
        },
      },
    ],
  },
  {
    id: "content",
    title: { es: "Organización y contenidos en Instagram", en: "Organisation and content on Instagram" },
    fields: [
      {
        id: "frustration",
        entry: 326955045,
        kind: "long",
        required: true,
        label: {
          es: "¿Qué es lo que más te frustra o te cuesta entender de Instagram hoy en día? (Métricas, el algoritmo, elegir los hashtags, etc.).",
          en: "What frustrates you most, or what do you find hardest to understand about Instagram right now? (Metrics, the algorithm, choosing hashtags, and so on.)",
        },
      },
      {
        id: "planning",
        entry: 1696159737,
        kind: "long",
        required: false,
        label: {
          es: "Al momento de pensar qué publicar en la semana, ¿cuál es tu mayor traba? ¿Te falta organización para planificar los días o te cuesta saber qué formato usar (Reel, Carrusel, foto sola)?",
          en: "When you think about what to publish this week, what’s the biggest block? Is it a lack of organisation to plan the days, or not knowing which format to use (Reel, carousel, single photo)?",
        },
      },
      {
        id: "format",
        entry: 485428648,
        kind: "single",
        required: false,
        label: {
          es: "¿Qué formato te cuesta más crear hoy o te da más timidez?",
          en: "Which format is hardest for you to create right now, or makes you the most self-conscious?",
        },
        options: [
          choice(
            "reels",
            "Hacer Reels (grabarme, editar, hablar a cámara).",
            "Making Reels (filming myself, editing, talking to camera).",
          ),
          choice(
            "stories",
            "Aparecer en Historias cotidianas.",
            "Appearing in everyday Stories.",
          ),
          choice(
            "captions",
            "Escribir los textos (despcripcion) de las publicaciones.",
            "Writing the captions for posts.",
            "Escribir los textos (descripción) de las publicaciones.",
          ),
          choice(
            "design",
            "Diseñar las imágenes o portadas.",
            "Designing the images or covers.",
          ),
          choice("all-formats", "Todos", "All of them"),
        ],
      },
      {
        id: "inspiration",
        entry: 1892629006,
        kind: "long",
        required: false,
        label: {
          es: "Decime 3 cuentas de Instagram (de tu mismo rubro o de otros) que te inspiren, te guste cómo se ven estéticamente o cómo venden sus productos.",
          en: "Name 3 Instagram accounts (in your field or another) that inspire you, whose look you like, or whose products sell in a way you admire.",
        },
      },
    ],
  },
  {
    id: "design",
    title: { es: "Diseño y herramientas", en: "Design and tools" },
    fields: [
      {
        id: "canva-level",
        entry: 598153725,
        kind: "single",
        required: false,
        label: {
          es: "¿Qué nivel de manejo tenés con la herramienta Canva?",
          en: "How comfortable are you with Canva?",
        },
        options: [
          choice("canva-zero", "No la usé nunca / Nivel cero.", "I’ve never used it / starting from zero."),
          choice(
            "canva-basic",
            "Nivel básico (sé cambiar un texto o usar una plantilla armada).",
            "Basic (I can change text or use a ready-made template).",
          ),
          choice(
            "canva-advanced",
            "Nivel intermedio/avanzado: Manejo bastante bien las herramientas (sé quitar fondos, agregar elementos, poner enlaces, crear videos).",
            "Intermediate/advanced: I’m fairly comfortable with the tools (I can remove backgrounds, add elements, add links, create videos).",
          ),
        ],
      },
      {
        id: "canva-plan",
        entry: 1760146198,
        kind: "single",
        required: false,
        label: {
          es: "¿Qué versión de Canva usás actualmente?",
          en: "Which version of Canva do you use right now?",
        },
        options: [
          choice("canva-free", "Canva Gratis.", "Canva Free."),
          choice("canva-pro", "Canva Pro (Pago).", "Canva Pro (paid)."),
        ],
      },
      {
        id: "identity",
        entry: 182755307,
        kind: "single",
        required: false,
        label: {
          es: "Sobre la identidad visual de tu marca:",
          en: "About your brand’s visual identity:",
        },
        options: [
          choice(
            "identity-yes",
            "Ya tengo definida mi paleta de colores y mis tipografías oficiales.",
            "I already have my colour palette and official typefaces defined.",
          ),
          choice(
            "identity-no",
            "Uso los colores y letras que me gustan en el momento, no tengo nada definido todavía.",
            "I use whatever colours and letters I like in the moment. Nothing is defined yet.",
          ),
        ],
      },
      {
        id: "design-block",
        entry: 1789462068,
        kind: "long",
        required: false,
        label: {
          es: "¿Qué es lo que más te cuesta o te traba al momento de diseñar un post o una historia para que se vea profesional?",
          en: "What do you find hardest, or what blocks you, when designing a post or a story so it looks professional?",
        },
      },
    ],
  },
  {
    id: "doubts",
    title: { es: "Tus dudas clave", en: "Your key questions" },
    description: {
      es: "Escribí acá abajo todas las dudas, consultas o miedos específicos que quieras que sí o sí tratemos en tu asesoría.",
      en: "Write every specific question, doubt or fear you want us to cover in your advisory.",
    },
    fields: [
      {
        id: "doubts",
        entry: 2086056311,
        kind: "long",
        required: true,
        label: { es: "Dudas", en: "Questions" },
      },
    ],
  },
];
